import { createFileRoute, Link } from "@tanstack/react-router";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { AionCta } from "@/components/site/AionCta";

const URL = "https://rajshamani.life/guides/how-to-become-an-ai-engineer-india";
const TITLE = "How to Become an AI Engineer in India (2026 Roadmap)";
const DESC =
  "A realistic 2026 roadmap to becoming an AI engineer in India: skills, tools, portfolio projects, salary expectations and where to start without a CS degree.";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Can I become an AI engineer in India without a computer science degree?",
    a: "Yes. Most AI engineering hiring in India in 2026 is portfolio-first — companies test whether you can ship working systems with LLM APIs, retrieval and agents. A CS degree helps for research roles, but application-layer AI engineering is judged on what you have built.",
  },
  {
    q: "How long does it take to become an AI engineer?",
    a: "With consistent daily practice, most people reach a hireable application-layer level in six to twelve months. The bottleneck is rarely theory — it is building and shipping enough real projects to prove judgement.",
  },
  {
    q: "What skills do AI engineers in India actually need in 2026?",
    a: "Python, strong prompt engineering, working knowledge of LLM APIs, retrieval-augmented generation, vector databases, agent frameworks, evaluation and cost control, plus enough backend skill to deploy what you build.",
  },
  {
    q: "Is prompt engineering still a real skill?",
    a: "Yes, but it has changed shape. Writing single clever prompts is no longer valuable; designing reliable multi-step systems, evaluating outputs and controlling cost and failure modes is what teams pay for.",
  },
  {
    q: "What should my first AI project be?",
    a: "Build something you would personally use — a document assistant over your own notes, an agent that automates one repetitive task in your job, or a research summariser. Working software beats a course certificate in every Indian AI interview.",
  },
];

export const Route = createFileRoute("/guides/how-to-become-an-ai-engineer-india")({
  head: () => ({
    meta: [
      { key: "title", title: TITLE },
      { key: "description", name: "description", content: DESC },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to Become an AI Engineer in India",
          description: DESC,
          author: { "@type": "Person", name: "Raj Shamani", url: "https://rajshamani.life" },
          step: [
            { "@type": "HowToStep", name: "Learn to talk to models properly", text: "Master prompt engineering: structure, context, constraints and evaluation." },
            { "@type": "HowToStep", name: "Learn Python and the API layer", text: "Enough Python to call LLM APIs, handle data and deploy a small service." },
            { "@type": "HowToStep", name: "Build retrieval systems", text: "Chunking, embeddings, vector search and grounding answers in real documents." },
            { "@type": "HowToStep", name: "Build agents", text: "Tool use, planning loops, error handling and cost control for multi-step tasks." },
            { "@type": "HowToStep", name: "Ship a public portfolio", text: "Three working projects with write-ups beat any certificate in Indian AI hiring." },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: GuidePage,
});

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-2xl md:text-3xl font-display text-gold mt-12 mb-4 scroll-mt-24">
      {children}
    </h2>
  );
}

function GuidePage() {
  return (
    <div>
      <PageBreadcrumb current="How to Become an AI Engineer in India" />
      <article className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-display font-semibold tracking-tight leading-tight">
          How to Become an AI Engineer in India
        </h1>
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
          The fastest-growing job category in Indian tech right now is not "machine
          learning researcher" — it is the application-layer AI engineer: someone who
          can take a language model and turn it into a product that works reliably,
          cheaply and safely. This is a practical 2026 roadmap for getting there,
          whether or not you studied computer science.
        </p>

        <H2 id="what-the-job-is">What an AI engineer actually does</H2>
        <p className="text-muted-foreground leading-relaxed">
          Very few AI jobs in India involve training models from scratch. The vast
          majority involve building on top of existing models: designing prompts and
          system instructions, connecting models to a company's own data, wiring in
          tools, evaluating output quality, and keeping token costs under control. The
          skill being hired for is judgement about systems, not mathematics.
        </p>

        <H2 id="skills">The skill stack, in order</H2>
        <ol className="mt-4 space-y-4 text-muted-foreground leading-relaxed list-decimal pl-5">
          <li>
            <strong className="text-foreground">Prompt engineering done seriously.</strong>{" "}
            Not tricks — structure, context management, constraints, and knowing how to
            tell whether an output is actually good.
          </li>
          <li>
            <strong className="text-foreground">Python and the API layer.</strong> Enough
            to call models, transform data, and stand up a small service.
          </li>
          <li>
            <strong className="text-foreground">Retrieval.</strong> Chunking strategies,
            embeddings, vector databases and grounding answers in a company's own
            documents. This is the single most requested skill in Indian job posts.
          </li>
          <li>
            <strong className="text-foreground">Agents and tool use.</strong> Multi-step
            workflows, function calling, retries, guardrails and failure handling.
          </li>
          <li>
            <strong className="text-foreground">Evaluation and cost control.</strong> How
            you prove a system works, and how you stop it burning money at scale.
          </li>
        </ol>

        <H2 id="roadmap">A six-month roadmap</H2>
        <p className="text-muted-foreground leading-relaxed">
          Months one and two: learn to work with models daily until prompting is
          instinctive, and get comfortable in Python. Months three and four: build a
          retrieval system over documents you care about, then a small agent that
          automates a real task in your own life or job. Months five and six: publish
          three projects with honest write-ups explaining what broke and how you fixed
          it, and start applying. In Indian AI hiring, that write-up is often what gets
          you the interview.
        </p>

        <H2 id="degree">You do not need another degree</H2>
        <p className="text-muted-foreground leading-relaxed">
          This mirrors a theme{" "}
          <Link to="/raj-shamani-education" className="text-gold underline underline-offset-4">
            Raj Shamani talks about constantly
          </Link>
          : he skipped the MBA and learned by building inside a real business. AI
          engineering rewards the same approach — the market checks what you shipped,
          not where you studied.
        </p>

        <H2 id="salary">What it pays</H2>
        <p className="text-muted-foreground leading-relaxed">
          Compensation moves quickly and varies by city and company type, so treat any
          single number with suspicion. The broad pattern in 2026 is that AI-focused
          engineers are paid meaningfully above equivalent-experience backend roles,
          with the largest gaps at startups shipping AI products and at global
          companies hiring remotely from India.
        </p>

        <H2 id="start">Where to start this week</H2>
        <p className="text-muted-foreground leading-relaxed">
          Pick one repetitive task you do every week and automate it with a model. Not a
          tutorial project — a real one. Then write down what went wrong. That single
          loop, repeated, is the entire job.
        </p>
      </article>

      <AionCta
        heading="Learn the AI skills this roadmap starts with"
        body="Aion is a cinematic AI course covering prompt engineering, AI agents and the mental models behind real output — built for people in India who want to use AI as leverage. Entry is ₹11."
      />
    </div>
  );
}
